import type { App } from "../app/App";
import { AppBrowser } from "../app/AppBrowser";
import { RuntimeEnvironmentGSEnum, RuntimeGS } from "../app/RuntimeGS";
import { StateConfigurationGS } from "./StateConfigurationGS";
import { StateBrowserRuntimeGS } from "./StateBrowserRuntimeGS";
import {
    StateConstructor,
    StateRunModeEnum,
    StateRunResultEnum,
    StateSetupGS
} from "./StateContractsGS";
import { StateRuntimeGS, StateRuntimeStoreGS } from "./StateRuntimeGS";
import { removeStateHeadTagGS, setStateHeadTagGS } from "./StateHeadGS";

/** State base and the only public facade for state configuration and execution. */
export abstract class StateGS {
    public static DefaultServerCacheMinutes = 30;
    /** Whether Server App replaces this route implementation with an SPA shell placeholder. */
    public static readonly IsPrivate: boolean = false;

    /** Configure and seal the state registry for the current application exactly once. */
    public static Configure(configure: (states: StateSetupGS) => void): void {
        const scope = RuntimeGS.current;
        if (!scope) throw new Error("StateGS.Configure must run during application initialization.");
        if (StateRuntimeStoreGS.has(scope.app))
            throw new Error("StateGS is already configured for this application.");
        if (!scope.stateConfigurationOpen)
            throw new Error("StateGS.Configure is only available from App.initializeCore().");

        const configuration = new StateConfigurationGS(scope.environment);
        configuration.apply(configure);

        let runtime: StateRuntimeGS;
        if (scope.stateRuntimeFactory) {
            runtime = scope.stateRuntimeFactory(scope.app, configuration);
        } else if (scope.environment === RuntimeEnvironmentGSEnum.Browser) {
            if (!(scope.app instanceof AppBrowser))
                throw new Error("Browser state configuration requires an AppBrowser instance.");
            runtime = new StateBrowserRuntimeGS(scope.app, configuration);
        } else throw new Error("The application environment did not provide a state runtime adapter.");
        StateRuntimeStoreGS.set(scope.app, runtime);
    }

    public static async Run(
        constructor: StateConstructor,
        args?: any,
        withHistory = true
    ): Promise<StateRunResultEnum> {
        if (!constructor) return StateRunResultEnum.Failed;
        return StateRuntimeStoreGS.require().run(constructor, args, { withHistory });
    }

    public static RunInstance(
        state: StateGS,
        args?: any,
        withHistory = true
    ): Promise<StateRunResultEnum> {
        return StateRuntimeStoreGS.require().runInstance(state, args, { withHistory });
    }

    public static RunByName(
        stateName: string,
        args?: any,
        withHistory = true
    ): Promise<StateRunResultEnum> {
        return StateRuntimeStoreGS.require().runByName(stateName, args, { withHistory });
    }

    public static RunByUrl(event?: Event, withHistory = true): Promise<StateRunResultEnum> {
        if (event) event.preventDefault();
        return StateRuntimeStoreGS.require().runByUrl(undefined, { withHistory });
    }

    public static RunByUrlArgs(
        args: Record<string, string>,
        _search?: string,
        event?: Event,
        withHistory = true
    ): Promise<StateRunResultEnum> {
        if (event) event.preventDefault();
        return StateRuntimeStoreGS.require().runByUrlArgs(args, { withHistory });
    }

    public static BuildUrl(stateName: string, args: Record<string, string> = {}): string {
        return StateRuntimeStoreGS.require().buildStateUrl(stateName, args);
    }

    public static get ActiveState(): StateGS | undefined {
        return StateRuntimeStoreGS.require().activeState;
    }

    public static get UrlStateArgName(): string {
        return StateRuntimeStoreGS.require().urlStateArgName;
    }

    public static get DefaultStateName(): string {
        return StateRuntimeStoreGS.require().defaultStateName;
    }

    public static get State404Name(): string {
        return StateRuntimeStoreGS.require().state404Name;
    }

    public static get UrlPathName(): string {
        return RuntimeGS.window?.location.pathname.toLowerCase() ?? "";
    }

    public static get UrlFullPath(): string {
        const runtimeWindow = RuntimeGS.window;
        return runtimeWindow ? runtimeWindow.location.origin.toLowerCase() + StateGS.UrlPathName : "";
    }

    private _name = "";
    private _title: string;
    private _active = false;
    private _cachePolicyResolved = false;
    protected readonly _urlArgs: Record<string, string> = {};

    private readonly _app?: App;
    public readonly inHistory: boolean;
    public serverCacheMinutes = -1;
    public description = "";
    public canonicalUrl = "";
    public robots = "";

    protected constructor(title: string, inHistory = true) {
        this._app = RuntimeGS.current?.app;
        this._title = title;
        this.inHistory = inHistory;
    }

    public get name(): string { return this._name; }
    public get title(): string { return this._title; }
    public set title(value: string) { this._title = value; }
    public get isActive(): boolean { return this._active; }
    public get urlArgs(): Readonly<Record<string, string>> { return this._urlArgs; }

    /** Application captured when the state was created. Projects may override this getter to narrow its type. */
    protected get app(): App | undefined { return this._app; }

    protected overrideName(name: string): void { this._name = name; }

    /** @internal */
    public initializeFrameworkState(name: string): void {
        this._name = name;
        if (!this._cachePolicyResolved) {
            if (this.serverCacheMinutes === -1 && StateGS.DefaultServerCacheMinutes !== -1)
                this.serverCacheMinutes = StateGS.DefaultServerCacheMinutes;
            this._cachePolicyResolved = true;
        }
    }

    /** @internal */
    public setFrameworkActive(active: boolean): void {
        this._active = active;
    }

    public async preStart(
        _mode: StateRunModeEnum,
        _args?: any
    ): Promise<StateRunResultEnum> {
        return StateRunResultEnum.Activated;
    }

    public async start(_args?: any): Promise<StateRunResultEnum> {
        return StateRunResultEnum.Activated;
    }

    public async afterStart(_mode: StateRunModeEnum, _args?: any): Promise<void> {
    }

    public async stop(): Promise<void> {
        this._active = false;
    }

    public updateUrl(): void {
        const runtime = StateRuntimeStoreGS.require(this.requireApp());
        if (!(runtime instanceof StateBrowserRuntimeGS))
            throw new Error("State URL updates are only available in a browser application.");
        runtime.updateStateUrl(this);
    }

    public getUrlArgs(): string {
        return StateRuntimeStoreGS.require(this.requireApp()).buildUrlForState(this);
    }

    /** Add or replace one state-owned head tag by its stable key. */
    public setHeadTag(
        key: string,
        tagName: "meta" | "link" | "script",
        attributes: Readonly<Record<string, string>> = {},
        textContent = ""
    ): void {
        setStateHeadTagGS(this, key, tagName, attributes, textContent);
    }

    /** Stop emitting the state-owned head tag with this key. */
    public removeHeadTag(key: string): void {
        removeStateHeadTagGS(this, key);
    }

    private requireApp(): App {
        const app = this._app ?? RuntimeGS.current?.app;
        if (!app) throw new Error("State is not attached to an application scope.");
        return app;
    }

}
